import { EKnowledgeProcessingStatus } from "@frontend-shared/types";
import type { ReactNode } from "react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface IKnowledgeStatusBadgeProps {
  status: EKnowledgeProcessingStatus;
  // NOTE: NSAI-7345 — shown before the label when the badge opens an
  // explanation (KnowledgeFailedStatusBadge).
  icon?: ReactNode;
  className?: string;
}

const STATUS_VARIANT_MAP: Record<
  EKnowledgeProcessingStatus,
  "default" | "secondary" | "destructive" | "success" | "warning"
> = {
  [EKnowledgeProcessingStatus.PENDING]: "secondary",
  [EKnowledgeProcessingStatus.PROCESSING]: "warning",
  [EKnowledgeProcessingStatus.COMPLETED]: "success",
  [EKnowledgeProcessingStatus.FAILED]: "destructive",
};

// NOTE: NSAI-7345 — translated, like the mobile badge (literal keys so
// react-i18next's strict typing checks them). The labels were English-only, and
// a badge that opens a tooltip names itself by its label.
const STATUS_LABEL_KEY_MAP = {
  [EKnowledgeProcessingStatus.PENDING]: "status.pending",
  [EKnowledgeProcessingStatus.PROCESSING]: "status.processing",
  [EKnowledgeProcessingStatus.COMPLETED]: "status.completed",
  [EKnowledgeProcessingStatus.FAILED]: "status.failed",
} as const;

const KnowledgeStatusBadgeComponent = ({
  status,
  icon,
  className,
}: IKnowledgeStatusBadgeProps) => {
  const { t } = useTranslation("knowledge");

  return (
    <Badge
      variant={STATUS_VARIANT_MAP[status]}
      className={cn(icon !== undefined && "gap-1", className)}
    >
      {icon}
      {t(STATUS_LABEL_KEY_MAP[status])}
    </Badge>
  );
};

export const KnowledgeStatusBadge = memo(KnowledgeStatusBadgeComponent);
